/*
 * Module-level hand-off for "open Apply Brand with this saved template
 * already selected" — used when the Asset Library's Brand Templates tab
 * routes a pick into Apply Brand instead of doing anything itself (no
 * insert/extract logic lives in the Asset Library; it only navigates).
 *
 * Mirrors integrations/tools/deckorate/nav-bridge.ts's park-and-consume shape:
 * the Asset Library (task pane side) parks the choice and asks DeckorateModule
 * to switch to "apply-brand" (via requestDeckorateView); ApplyBrandPanel reads
 * and clears the park on its own mount, the same way DeckorateModule reads
 * nav-bridge's pending target on its mount.
 */

import type { SavedBrandTemplate } from "./brandTemplatesApi";
import type { MyBrandTemplate } from "./myBrandTemplatesStorage";

export type PendingSavedTemplateSelection =
  | { readonly kind: "org"; readonly template: SavedBrandTemplate }
  | { readonly kind: "local"; readonly template: MyBrandTemplate };

let pending: PendingSavedTemplateSelection | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();

/** Parks a selection made outside ApplyBrandPanel (the Asset Library), and
 *  notifies any already-mounted ApplyBrandPanel so a pick made while it's
 *  already open (e.g. Switch Template from the Brand Templates preview) is
 *  applied immediately, not just on a future mount. */
export function setPendingSavedTemplateSelection(selection: PendingSavedTemplateSelection): void {
  pending = selection;
  for (const listener of listeners) listener();
}

/** Reads and clears the parked selection. Called once by ApplyBrandPanel on
 *  mount — clearing here stops a stale pick from hijacking a later, ordinary
 *  visit to Apply Brand. */
export function consumePendingSavedTemplateSelection(): PendingSavedTemplateSelection | null {
  const selection = pending;
  pending = null;
  return selection;
}

/** Subscribed by an already-mounted ApplyBrandPanel so a later pick is
 *  applied live instead of waiting for a future mount. Returns an unsubscribe
 *  for effect cleanup. */
export function subscribePendingSavedTemplateSelection(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// ─── "This previewed template is no longer available" (BRD EC-08) ──────────
// The Brand Templates preview (opened from Apply Brand's eye icon) notifies
// ApplyBrandPanel the moment the organisation withdraws the template it's
// showing, so that by the time the user closes the preview, the pane has
// already cleared the selection (if it matched) and shown the EC-04-style
// message — no separate "did you close yet?" round trip needed.

type UnavailableListener = (id: string) => void;
const unavailableListeners = new Set<UnavailableListener>();

/** Called by the Brand Templates preview (dialog or inline) as soon as it
 *  notices the template it's showing has disappeared from the library. */
export function notifyPreviewedTemplateUnavailable(id: string): void {
  for (const listener of unavailableListeners) listener(id);
}

/** Subscribed by ApplyBrandPanel. Returns an unsubscribe for effect cleanup. */
export function subscribePreviewedTemplateUnavailable(listener: UnavailableListener): () => void {
  unavailableListeners.add(listener);
  return () => {
    unavailableListeners.delete(listener);
  };
}
